/**
 * Phase 8.2 — Student Revision Tasks widget.
 * Shows pending revision tasks assigned by tutors + a completion action.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, ArrowRight, ClipboardCheck, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const StudentRevisionTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await ax.get('/api/me/student/revision-tasks?status=pending');
      setTasks(r.data.items || []);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const complete = async (id) => {
    setBusy(id);
    try {
      await ax.post(`/api/me/student/revision-tasks/${id}/complete`, { completed: true });
      toast.success('Revision marked complete');
      setTasks((t) => t.filter(x => x.task_id !== id));
    } catch (_e) { toast.error('Could not mark complete'); }
    finally { setBusy(null); }
  };

  if (loading) return null;
  if (tasks.length === 0) return null;
  return (
    <section className="bg-red-50 border border-red-200 rounded-2xl p-4" data-testid="student-revision-tasks">
      <div className="flex items-center gap-2 mb-2">
        <ClipboardCheck className="h-4 w-4 text-red-800" />
        <div className="text-xs uppercase tracking-wider text-red-800 font-bold">Revision from your tutor · {tasks.length}</div>
      </div>
      <ul className="space-y-2">
        {tasks.map((t) => {
          const link = t.resource_kind === 'topic' ? `/student/learning?topic=${t.resource_slug}`
            : t.resource_kind === 'mock' ? `/student/exams?mock=${t.resource_slug}`
            : '/resources/selective-entry';
          return (
            <li key={t.task_id} className="bg-white border border-red-100 rounded-xl p-3 flex items-center gap-3" data-testid={`revision-task-${t.task_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-gray-900 truncate">{t.resource_title || t.resource_slug}</div>
                <div className="text-[11px] text-gray-500 truncate">
                  Assigned by <span className="font-semibold text-gray-700">{t.assigned_by_name || t.assigned_by}</span>
                  {t.note ? ` — "${t.note}"` : ''}
                </div>
              </div>
              <Link to={link} data-testid={`revision-open-${t.task_id}`}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-red-700 hover:text-red-900">
                Open <ArrowRight className="h-3 w-3" />
              </Link>
              <button onClick={() => complete(t.task_id)} disabled={busy === t.task_id}
                data-testid={`revision-complete-${t.task_id}`}
                className="inline-flex items-center gap-1 text-[11px] font-bold px-3 h-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40">
                {busy === t.task_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Mark done
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default StudentRevisionTasks;
